import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { config } from '../config';
import './Layout.css';

function Layout({ children, tenantId, tenants, onTenantChange, user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [clickedDropdown, setClickedDropdown] = useState(null);
  const [testDataCredentials, setTestDataCredentials] = useState(null);
  const navRef = useRef(null);
  const subNavRef = useRef(null);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenantId');
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/dashboard' || path === '/') {
      // Dashboard ist nur aktiv, wenn wir auf '/dashboard' sind UND keine Kategorie aktiv ist
      return (location.pathname === '/dashboard' || location.pathname === '/') && !clickedDropdown;
    }
    return location.pathname === path;
  };
  
  const isInCategory = (paths) => paths.some(path => location.pathname === path || location.pathname.startsWith(path + '/'));

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isNavClick = navRef.current && navRef.current.contains(event.target);
      const isSubNavClick = subNavRef.current && subNavRef.current.contains(event.target);
      
      // Prüfe, ob der Klick auf einen Button, Link oder expandierbares Element ist
      const isInteractiveElement = event.target.closest('.data-table') || 
                                   event.target.closest('.icon-button') ||
                                   event.target.closest('button') ||
                                   event.target.closest('a') ||
                                   event.target.closest('[style*="cursor: pointer"]') ||
                                   event.target.closest('.customer-cars') ||
                                   event.target.closest('.car-journal-section') ||
                                   event.target.closest('.repair-history-section') ||
                                   event.target.closest('.repair-history-header') ||
                                   event.target.closest('.car-repair-summary') ||
                                   event.target.closest('.car-journal-header') ||
                                   event.target.closest('.detail-section');
      
      // Schließe den Sub-Nav NIE - er bleibt immer sichtbar, wenn eine Kategorie aktiv ist
      // Der Sub-Nav wird nur geschlossen, wenn man auf eine andere Kategorie klickt oder explizit schließt
      // NICHT wenn man auf Links im Sub-Nav klickt
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Der Sub-Nav bleibt immer sichtbar, wenn eine Kategorie aktiv ist
    if (location.pathname === '/dashboard' || location.pathname === '/') {
      setClickedDropdown('dashboard');
    } else if (isInCategory(['/customers', '/cars'])) {
      setClickedDropdown('stammdaten');
    } else if (isInCategory(['/repairs', '/invoices', '/service-templates'])) {
      setClickedDropdown('auftraege');
    } else if (isInCategory(['/team', '/locations', '/inventory', '/purchases', '/admin/users', '/appointments', '/reports', '/maintenance-plans'])) {
      setClickedDropdown('werkstatt');
    } else if (isInCategory(['/roles', '/tags', '/settings', '/api-tokens'])) {
      setClickedDropdown('einstellungen');
    } else if (isInCategory(['/fleet'])) {
      setClickedDropdown('fleet');
    }
    // clickedDropdown wird NICHT auf null gesetzt, damit der Sub-Nav immer sichtbar bleibt
  }, [location.pathname]);

  useEffect(() => {
    const loadTestDataCredentials = async () => {
      try {
        const apiUrl = config.API_URL || '/api';
        const response = await fetch(`${apiUrl}/config/testdata-credentials`);
        if (response.ok) {
          const data = await response.json();
          console.log('Test data credentials loaded:', data);
          setTestDataCredentials(data);
        } else {
          console.error('Failed to load test data credentials:', response.status);
        }
      } catch (error) {
        console.error('Error loading test data credentials:', error);
      }
    };
    loadTestDataCredentials();
  }, []);

  const handleDropdownClick = (dropdownId) => {
    // Wenn man auf eine andere Kategorie klickt, wechsle zu dieser
    if (clickedDropdown !== dropdownId) {
      setClickedDropdown(dropdownId);
      
      // Navigiere automatisch zum ersten Punkt des 2nd-Layers
      const subNavItems = getSubNavItemsForCategory(dropdownId);
      if (subNavItems.length > 0) {
        navigate(subNavItems[0].path);
      }
    } else {
      // Wenn man auf die gleiche Kategorie klickt, navigiere zum ersten Punkt
      const subNavItems = getSubNavItemsForCategory(dropdownId);
      if (subNavItems.length > 0) {
        navigate(subNavItems[0].path);
      }
    }
    // clickedDropdown wird NICHT auf null gesetzt, damit der Sub-Nav immer sichtbar bleibt
    setActiveDropdown(null);
  };
  
  const getSubNavItemsForCategory = (categoryId) => {
    if (categoryId === 'dashboard') {
      return [
        { path: '/dashboard', label: 'Allgemein' }
      ];
    } else if (categoryId === 'fleet') {
      return [
        { path: '/fleet/inventory', label: 'Bestand' },
        { path: '/fleet/sales', label: 'Verkäufe' }
      ];
    } else if (categoryId === 'stammdaten') {
      return [
        { path: '/customers', label: 'Kunden' },
        { path: '/cars', label: 'Autos' }
      ];
    } else if (categoryId === 'auftraege') {
      return [
        { path: '/repairs', label: 'Reparaturen' },
        { path: '/invoices', label: 'Rechnungen' },
        { path: '/service-templates', label: 'Standardaufträge' }
      ];
    } else if (categoryId === 'werkstatt') {
      const items = [
        { path: '/team', label: 'Team' },
        { path: '/maintenance-plans', label: 'Wartungspläne' },
        { path: '/locations', label: 'Werkstattorte' },
        { path: '/inventory', label: 'Inventar' },
        { path: '/purchases', label: 'Anschaffungen' },
        { path: '/appointments', label: 'Termine' },
        { path: '/reports', label: 'Berichte' }
      ];
      if (user && user.role === 'tenantAdmin') {
        items.push({ path: '/admin/users', label: 'Benutzer' });
      }
      return items;
    } else if (categoryId === 'einstellungen') {
      return [
        { path: '/roles', label: 'Rollen' },
        { path: '/tags', label: 'Tags' },
        { path: '/equipment', label: 'Sonderaustattungen' },
        { path: '/settings', label: 'Allgemein' },
        { path: '/api-tokens', label: 'API' }
      ];
    }
    return [];
  };

  const isCategoryActive = (categoryId) => {
    // Wenn diese Kategorie gerade geklickt wurde, ist sie aktiv
    if (clickedDropdown === categoryId) {
      return true;
    }
    // Ansonsten prüfe, ob die aktuelle Route zu dieser Kategorie gehört
    if (categoryId === 'dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    } else if (categoryId === 'stammdaten') {
      return isInCategory(['/customers', '/cars']);
    } else if (categoryId === 'auftraege') {
      return isInCategory(['/repairs', '/invoices', '/service-templates']);
    } else if (categoryId === 'werkstatt') {
      return isInCategory(['/team', '/locations', '/inventory', '/purchases', '/admin/users', '/appointments', '/reports', '/maintenance-plans']);
    } else if (categoryId === 'einstellungen') {
      return isInCategory(['/roles', '/tags', '/equipment', '/settings', '/api-tokens']);
    } else if (categoryId === 'fleet') {
      return isInCategory(['/fleet']);
    }
    return false;
  };

  const getSubNavItems = () => {
    if (!clickedDropdown) return [];
    return getSubNavItemsForCategory(clickedDropdown);
  };

  return (
    <div className="layout">
      <header className="header">
        <div className="header-content">
          <div className="logo-section">
            <Link to="/dashboard" className="logo-link">
              <h1 className="logo">
                <span style={{ color: '#333' }}>Garage</span>
                <span style={{ color: '#ff9800' }}>Desk</span>
              </h1>
            </Link>
          </div>
          <div className="header-right">
            {tenants.length > 1 ? (
              <select 
                value={tenantId} 
                onChange={(e) => onTenantChange(e.target.value)}
                className="tenant-select"
              >
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            ) : tenants.length === 1 ? (
              <div className="tenant-name">
                {tenants[0].name}
                <Link to="/settings" className="tenant-edit-link">Bearbeiten</Link>
              </div>
            ) : null}
            {user && (
              <div className="user-info">
                <span>
                  {user.username}
                  {user.userRole && <span className="user-role"> ({user.userRole})</span>}
                </span>
                <button onClick={handleLogout} className="btn-small btn-secondary">
                  Abmelden
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      
      <nav className="nav" ref={navRef}>
        <span 
          className={`nav-category ${isCategoryActive('dashboard') ? 'active' : ''} ${clickedDropdown === 'dashboard' ? 'open' : ''}`}
          onClick={() => handleDropdownClick('dashboard')}
        >
          Dashboard
        </span>
        
        <span 
          className={`nav-category ${isCategoryActive('fleet') ? 'active' : ''} ${clickedDropdown === 'fleet' ? 'open' : ''}`}
          onClick={() => handleDropdownClick('fleet')}
        >
          Fahrzeuge
        </span>
        
        <span 
          className={`nav-category ${isCategoryActive('stammdaten') ? 'active' : ''} ${clickedDropdown === 'stammdaten' ? 'open' : ''}`}
          onClick={() => handleDropdownClick('stammdaten')}
        >
          Stammdaten
        </span>

        <span 
          className={`nav-category ${isCategoryActive('auftraege') ? 'active' : ''} ${clickedDropdown === 'auftraege' ? 'open' : ''}`}
          onClick={() => handleDropdownClick('auftraege')}
        >
          Aufträge
        </span>

        <span 
          className={`nav-category ${isCategoryActive('werkstatt') ? 'active' : ''} ${clickedDropdown === 'werkstatt' ? 'open' : ''}`}
          onClick={() => handleDropdownClick('werkstatt')}
        >
          Werkstatt
        </span>

        <span 
          className={`nav-category ${isCategoryActive('einstellungen') ? 'active' : ''} ${clickedDropdown === 'einstellungen' ? 'open' : ''}`}
          onClick={() => handleDropdownClick('einstellungen')}
        >
          Einstellungen
        </span>

      </nav>

      {clickedDropdown && (
        <nav className="sub-nav" ref={subNavRef}>
          {getSubNavItems().map(item => (
            <Link 
              key={item.path}
              to={item.path} 
              className={isActive(item.path) ? 'active' : ''}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {item.label}
            </Link>
          ))}
        </nav>
      )}

      <main className="main">
        {children}
      </main>
      
      <footer className="footer">
        <div className="footer-content">
          <Link 
            to="/login?demo=true" 
            className="footer-link demo-link"
            style={{ fontWeight: '600', color: '#2196f3' }}
          >
            Werkstatt Demo
          </Link>
          <Link 
            to="/customer-login?email=testkunde@example.com&password=test123" 
            className="footer-link demo-link"
            style={{ fontWeight: '600', color: '#2196f3' }}
          >
            Kunde Demo
          </Link>
          <span className="footer-separator">|</span>
          <Link to="/support" className="footer-link">
            Support
          </Link>
          <Link to="/impressum" className="footer-link">
            Impressum
          </Link>
          <Link to="/agbs" className="footer-link">
            AGBs
          </Link>
          <Link to="/datenschutz" className="footer-link">
            Datenschutz
          </Link>
          <Link to="/documentation" className="footer-link">
            Dokumentation
          </Link>
          <Link to="/api-documentation" className="footer-link">
            API
          </Link>
        </div>
      </footer>
    </div>
  );
}

export default Layout;

